/**
 * BMW Partner Hub menu entries ("BMW Fleet" until 2026-10-18, briefly "Developer Center"; renamed
 * because the section serves the integration team and BMW's business users alike, D-48).
 *
 * Layout: Overview, then one folder per area of the BMW technical kickoff (A-I) - Integration
 * (central for every area), Fleet Setup, Vehicles & Drivers, Fuelling, Sites & Stations, Car Wash,
 * Fuel Cards, Invoicing, Support. Pages marked "planned" are placeholders that show the intended
 * scope (shared/bmw-planned-pages.ts). The `sectionId` stays `bmw-fleet` so users keep their saved
 * favourite and expanded state.
 *
 * This array owns a dedicated head title. Do not remove it: sidebar
 * sections are established by the most recent head-title item, so entries with
 * no heading can appear under an unrelated section when an earlier heading is
 * filtered out for the current user.
 *
 * ## Who sees these entries
 *
 * `requiredUserGroup: ['bmwfleet']` **and** a role (D-48):
 *   - integration pages (API Reference, API Guide, Shared Tools, Whitelist): `developer`;
 *   - pages for business users too (Overview, Fuel Cards, Permission Groups, Stations):
 *     `developer` or `fuel_card_manager` (the sidebar takes any listed role; the routes use
 *     `requiredAnyRole` for the same meaning);
 *   - Order a Card: `fuel_card_manager` only, as every fuel-card change in rp-integrations.
 * The group alone is not enough: `bmwfleet` also contains BMW **fleet managers**,
 * and these pages are integration tooling rather than something a fleet manager
 * should be handed. Both guards are repeated on every entry because
 * `navmenu.vue` evaluates each item in isolation — a group whose children are
 * gated but whose parent is not would still show an empty expander.
 *
 * The role has to be grantable to reach this audience, and it was not: `roles.ts`
 * declared `developer` as `visibleToPartners: [RYD_INTERNAL]`, and
 * `mixins/user-management/userFormMixin.js` strips any role whose visibility is
 * *exactly* `[RYD_INTERNAL]` from a non-`ryd` target
 * (`filterOutRydInternalRoles`), so it never appeared on `/users/edit` for a BMW
 * user. `FLEET_PARTNER` has since been added to that list — bmwfleet users carry
 * the fleet partner type — which is what makes this gate reachable. It still does
 * not hand them the internal developer hub: every entry there also requires
 * `requiredUserGroup: ['ryd']`.
 *
 * One more rule of that form, worth knowing before changing this: it only offers
 * roles the **acting admin holds themselves**, so a brand-new role would have
 * nobody able to grant it.
 *
 * Mind the asymmetry between the two enforcement points: the router checks
 * `requiredRoles.every(...)` while `navmenu.vue` checks `.some(...)`. Entries listing two roles
 * here therefore pair with `requiredAnyRole` on their route, never with `requiredRoles`.
 *
 * Deliberately **no** `requiredPartnerTypes`. It only creates a second way to
 * fail: a portal user that reaches the app through the migration path in
 * `services/firebaseAuthService.js` gets `partnerTypes: ['API_PARTNER']`
 * hardcoded regardless of their record, and gating on the partner type once hid
 * these entries from exactly the BMW user they were built for.
 *
 * Keep every gate in sync with
 * `src/router/routes/bmw-individual-partner.routes.ts` — a menu entry the route
 * guard then rejects is worse than no menu entry at all. See
 * `src/pages/individual-partners/bmw/INTEGRATION-NOTES.md`.
 */

import { Roles } from '../../roles';
import { MenuItem } from '../types';

export const bmwSection: MenuItem[] = [
  {
    headTitle1: 'Partner Hub',
    headTitle2: 'APIs, fuel cards and monitoring for BMW',
    sectionId: 'bmw-fleet',
    type: 'headtitle',
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager, Roles.bmw_circle_k_pcs],
  },
  {
    path: '/individual-partners/bmw',
    title: 'Overview',
    icon: 'stroke-home',
    iconf: 'fill-home',
    type: 'link',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
  },
  {
    // Central for every area.
    title: 'Integration',
    icon: 'stroke-api-integration-widget',
    iconf: 'fill-api-integration-widget',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer],
    children: [
      {
        // One spec for every area (auth, Whitelist API, fuel-card API), fetched on every visit.
        path: '/individual-partners/bmw/api-reference',
        title: 'API Reference',
        type: 'link',
        icon: 'stroke-api-integration-widget',
        iconf: 'fill-api-integration-widget',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        path: '/individual-partners/bmw/api-guide',
        title: 'API Guide',
        type: 'link',
        icon: 'stroke-info',
        iconf: 'fill-info',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        path: '/individual-partners/bmw/api-interaction-log',
        title: 'API Log',
        type: 'link',
        icon: 'stroke-data-exchange-monitoring',
        iconf: 'fill-data-exchange-monitoring',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        // Own gate like its route and the rp-users proxy; allowed at this second level.
        path: '/integration-and-settings/api-credentials',
        title: 'API Credentials',
        type: 'link',
        icon: 'stroke-secure-file',
        iconf: 'fill-secure-file',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.api_credentials_admin],
      },
    ],
  },
  {
    // Area A: fleet set-up and permissions.
    title: 'Fleet Setup',
    icon: 'stroke-user',
    iconf: 'fill-user',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
    children: [
      {
        path: '/individual-partners/bmw/permission-groups',
        title: 'Permission Groups',
        type: 'link',
        icon: 'stroke-user',
        iconf: 'fill-user',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/fleet-configuration',
        title: 'Configuration',
        type: 'link',
        icon: 'stroke-task',
        iconf: 'fill-task',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
    ],
  },
  {
    // Area B: whitelist and onboarding, sent by TIM through the API.
    title: 'Vehicles & Drivers',
    icon: 'stroke-fleet',
    iconf: 'fill-fleet',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer],
    children: [
      {
        path: '/individual-partners/bmw/vins',
        title: 'Vehicles',
        type: 'link',
        icon: 'stroke-fleet',
        iconf: 'fill-fleet',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        path: '/individual-partners/bmw/drivers',
        title: 'Drivers',
        type: 'link',
        icon: 'stroke-user',
        iconf: 'fill-user',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/driver-onboarding',
        title: 'Driver Onboarding',
        type: 'link',
        icon: 'stroke-user',
        iconf: 'fill-user',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        path: '/individual-partners/bmw/sync-monitor',
        title: 'Sync Monitor',
        type: 'link',
        icon: 'stroke-data-exchange-monitoring',
        iconf: 'fill-data-exchange-monitoring',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        path: '/individual-partners/bmw/change-history',
        title: 'Change History',
        type: 'link',
        icon: 'stroke-project',
        iconf: 'fill-project',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
      {
        path: '/individual-partners/bmw/whitelist-batch-guide',
        title: 'Batch API Guide',
        type: 'link',
        icon: 'stroke-info',
        iconf: 'fill-info',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer],
      },
    ],
  },
  {
    // Areas C and D: mobile and in-car app.
    title: 'Fuelling',
    icon: 'stroke-charts',
    iconf: 'fill-charts',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
    children: [
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/payment-channels',
        title: 'Payment Channels',
        type: 'link',
        icon: 'stroke-charts',
        iconf: 'fill-charts',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
    ],
  },
  {
    // Area E: supply and sites.
    title: 'Sites & Stations',
    icon: 'stroke-maps',
    iconf: 'fill-maps',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
    children: [
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/station-coverage',
        title: 'Station Coverage',
        type: 'link',
        icon: 'stroke-maps',
        iconf: 'fill-maps',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // TEMPORARY: Standortplan 13.10.2026; remove with the page.
        path: '/individual-partners/bmw/station-transition-plan',
        title: 'Transition Plan',
        type: 'link',
        icon: 'stroke-maps',
        iconf: 'fill-maps',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
    ],
  },
  {
    // Area F.
    title: 'Car Wash',
    icon: 'stroke-fleet',
    iconf: 'fill-fleet',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager, Roles.bmw_circle_k_pcs],
    children: [
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/car-wash',
        title: 'Wash Overview',
        type: 'link',
        icon: 'stroke-fleet',
        iconf: 'fill-fleet',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // SmartNow and Christ licence-plate sales registered in the Circle K POS (DFS PCS).
        // Read-only PCS API log from BigQuery; role bmw_circle_k_pcs (rp-integrations).
        path: '/individual-partners/bmw/car-wash/circle-k-pos-reporting',
        title: 'Circle K Reporting',
        type: 'link',
        icon: 'stroke-ecommerce',
        iconf: 'fill-ecommerce',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.bmw_circle_k_pcs],
      },
      {
        // Diagrams from ryd-portal-guides via rp-users (role bmw_circle_k_pcs).
        path: '/individual-partners/bmw/car-wash/circle-k-pcs-how-it-works',
        title: 'Circle K Explained',
        type: 'link',
        icon: 'stroke-learning',
        iconf: 'fill-learning',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.bmw_circle_k_pcs],
      },
    ],
  },
  {
    // Area G: ARAL plastic cards.
    title: 'Fuel Cards',
    icon: 'stroke-ecommerce',
    iconf: 'fill-ecommerce',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
    children: [
      {
        // List, detail, PIN change, block - everything the fuel-card API offers (D-47).
        path: '/individual-partners/bmw/fuel-cards',
        title: 'Card Orders',
        type: 'link',
        icon: 'stroke-ecommerce',
        iconf: 'fill-ecommerce',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        path: '/individual-partners/bmw/fuel-cards/new',
        title: 'Order a Card',
        type: 'link',
        icon: 'stroke-ecommerce',
        iconf: 'fill-ecommerce',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.fuel_card_manager],
      },
      {
        path: '/individual-partners/bmw/fuel-card-process',
        title: 'Card Process',
        type: 'link',
        icon: 'stroke-ecommerce',
        iconf: 'fill-ecommerce',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // Every order flow and status against the BP simulator
        path: '/individual-partners/bmw/fuel-cards/staging-tests',
        title: 'Staging Tests',
        type: 'link',
        icon: 'stroke-ecommerce',
        iconf: 'fill-ecommerce',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
    ],
  },
  {
    // Area H: transactions, reconciliation and invoicing.
    title: 'Invoicing',
    icon: 'stroke-transaction-exchange',
    iconf: 'fill-transaction-exchange',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
    children: [
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/transactions',
        title: 'Transactions',
        type: 'link',
        icon: 'stroke-transaction-exchange',
        iconf: 'fill-transaction-exchange',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/reconciliation',
        title: 'Reconciliation',
        type: 'link',
        icon: 'stroke-transaction-exchange',
        iconf: 'fill-transaction-exchange',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/invoices',
        title: 'Invoices',
        type: 'link',
        icon: 'stroke-transaction-exchange',
        iconf: 'fill-transaction-exchange',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
    ],
  },
  {
    // Area I: fraud, anomalies and support.
    title: 'Support',
    icon: 'stroke-support-tickets',
    iconf: 'fill-support-tickets',
    type: 'sub',
    active: false,
    requiredUserGroup: ['bmwfleet'],
    requiredRoles: [Roles.developer, Roles.fuel_card_manager],
    children: [
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/anomaly-reports',
        title: 'Anomaly Reports',
        type: 'link',
        icon: 'stroke-support-tickets',
        iconf: 'fill-support-tickets',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
      {
        // Planned placeholder.
        path: '/individual-partners/bmw/support',
        title: 'Support Process',
        type: 'link',
        icon: 'stroke-support-tickets',
        iconf: 'fill-support-tickets',
        requiredUserGroup: ['bmwfleet'],
        requiredRoles: [Roles.developer, Roles.fuel_card_manager],
      },
    ],
  },
];
